// web/src/utils/passwordPolicy.js
// Shared password policy logic — presets, validation, strictest-merge

export const SECURITY_PRESETS = {
  basic: {
    min_length: 8,
    require_uppercase: false,
    require_lowercase: true,
    require_number: false,
    require_special: false,
    require_mfa: false,
  },
  standard: {
    min_length: 10,
    require_uppercase: true,
    require_lowercase: true,
    require_number: true,
    require_special: false,
    require_mfa: false,
  },
  strong: {
    min_length: 12,
    require_uppercase: true,
    require_lowercase: true,
    require_number: true,
    require_special: true,
    require_mfa: false,
  },
  strong_mfa: {
    min_length: 12,
    require_uppercase: true,
    require_lowercase: true,
    require_number: true,
    require_special: true,
    require_mfa: true,
  },
};

export const PRESET_LABELS = {
  basic: 'Basic',
  standard: 'Standard',
  strong: 'Strong',
  strong_mfa: 'Strong + MFA',
  custom: 'Custom',
};

export const DEFAULT_SECURITY_POLICY = {
  staff: { preset: 'basic', ...SECURITY_PRESETS.basic },
  customers: { preset: 'basic', ...SECURITY_PRESETS.basic },
};

/**
 * Get the full policy config for a preset name.
 * Returns the basic preset for unknown names.
 */
export function getPresetConfig(presetName) {
  return SECURITY_PRESETS[presetName] || SECURITY_PRESETS.basic;
}

/**
 * Validate a password against a policy.
 * Returns { valid: boolean, errors: string[] }
 */
export function validatePassword(password, policy) {
  const errors = [];
  const p = policy || SECURITY_PRESETS.basic;

  if (!password || password.length < (p.min_length || 8)) {
    errors.push(`Must be at least ${p.min_length || 8} characters`);
  }
  if (p.require_lowercase && !/[a-z]/.test(password)) {
    errors.push('Must contain a lowercase letter');
  }
  if (p.require_uppercase && !/[A-Z]/.test(password)) {
    errors.push('Must contain an uppercase letter');
  }
  if (p.require_number && !/[0-9]/.test(password)) {
    errors.push('Must contain a number');
  }
  if (p.require_special && !/[^A-Za-z0-9]/.test(password)) {
    errors.push('Must contain a special character');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Get a list of password requirements with their met/unmet status.
 * Returns [{ label: string, met: boolean }]
 */
export function getPasswordRequirements(password, policy) {
  const p = policy || SECURITY_PRESETS.basic;
  const pwd = password || '';
  const requirements = [];

  requirements.push({
    label: `At least ${p.min_length || 8} characters`,
    met: pwd.length >= (p.min_length || 8),
  });

  if (p.require_lowercase) {
    requirements.push({ label: 'Lowercase letter', met: /[a-z]/.test(pwd) });
  }
  if (p.require_uppercase) {
    requirements.push({ label: 'Uppercase letter', met: /[A-Z]/.test(pwd) });
  }
  if (p.require_number) {
    requirements.push({ label: 'Number', met: /[0-9]/.test(pwd) });
  }
  if (p.require_special) {
    requirements.push({ label: 'Special character', met: /[^A-Za-z0-9]/.test(pwd) });
  }

  return requirements;
}

/**
 * Merge multiple security policies and return the strictest union.
 * Takes max of min_length, OR of all require_* flags.
 */
export function mergeStrictestPolicies(policies) {
  const validPolicies = (policies || []).filter(Boolean);
  if (validPolicies.length === 0) {
    return { ...SECURITY_PRESETS.basic, require_mfa: false };
  }

  return {
    min_length: Math.max(...validPolicies.map(p => p.min_length || 8)),
    require_lowercase: validPolicies.some(p => p.require_lowercase),
    require_uppercase: validPolicies.some(p => p.require_uppercase),
    require_number: validPolicies.some(p => p.require_number),
    require_special: validPolicies.some(p => p.require_special),
    require_mfa: validPolicies.some(p => p.require_mfa),
  };
}

/**
 * Map a user role to "staff" or "customer" for policy lookup.
 */
export function getUserType(role) {
  return role === 'customer' ? 'customers' : 'staff';
}
